// §7.1 / §11.7 — what is on the desktop (D-276, D-277).
//
// The desktop **is** `/Users/<user>/Desktop`. There is no second model: an app icon is a `.hlnk`
// file (§shortcut), a group is a real directory, a document is the document. That single choice is
// what makes the three things this service offers cheap — grouping is `mkdir` + `move`, renaming
// is `move`, and adding an icon is `write` — and it is what makes them *consistent*, because
// Explorer is looking at the same bytes and §7.7's host-OS drop has always landed here.
//
// It lives in the kernel rather than in a skin because two skins render it and §6.5 forbids a skin
// importing an app or another skin. It reads through `VfsTree` rather than `HangarAPI.fs` because
// the shell is the thing that does the granting (§9.5), not a thing that asks.
import {
  USER_FOLDERS,
  basename,
  isShortcutName,
  joinVfs,
  parentOf,
  parseShortcut,
  serializeShortcut,
  shortcutFileName,
  shortcutLabel,
  uniqueName,
  userRoot,
  type FileEntry,
  type VfsPath,
} from '@hangar/shared';
import type { Bus, Unsubscribe } from '../bus';
import type { VfsTree } from '../vfs/tree';

/** One thing on the desktop. */
export interface DesktopItem {
  /** Where it lives. Also its React key — a path is unique within a directory by construction. */
  path: VfsPath;
  /** What the user sees under the icon. A shortcut sheds its extension; nothing else does. */
  label: string;
  kind: 'app' | 'folder' | 'file';
  /** The app a shortcut names. Present only for `kind: 'app'`. */
  appId?: string;
  /**
   * A shortcut whose app is not registered — uninstalled, or gated behind sign-in (§24.11). The
   * icon still draws, dimmed: silently hiding it would make an app the user put there vanish with
   * no way to tell whether it was removed or is merely unavailable.
   */
  unresolved?: boolean;
  isDir: boolean;
  mtime: number;
}

export interface DesktopService {
  /** The current contents, ordered folders-first then by name. */
  list(dir?: VfsPath): Promise<DesktopItem[]>;
  /** Fires whenever the folder changes — by this tab, another tab (§18 F16), or a sign-in. */
  subscribe(handler: () => void): Unsubscribe;
  /** `/Users/<user>/Desktop`, re-read per call so it follows sign-in (§7.1). */
  root(): VfsPath;
  /** Put an app on the desktop. Returns the shortcut's path; a duplicate gets "(2)" (D-045). */
  addApp(appId: string): Promise<VfsPath>;
  /** Which apps are already on the desktop, so the "Add" menu can say so. */
  appsPresent(): Promise<ReadonlySet<string>>;
  /**
   * Can this path be opened as a folder?
   *
   * Not the same question as "does it exist": the games' group is one of the default icons, so on
   * a desktop nobody has arranged it is openable and has no directory behind it (D-289).
   */
  isFolder(path: VfsPath): Promise<boolean>;
  /**
   * Drag one icon onto another. Dropping onto a **folder** moves it in; dropping onto anything
   * else makes a new folder holding both — the gesture that creates a group.
   *
   * Returns the folder that now holds them, so the caller can ask what it should be called.
   */
  group(sourcePath: VfsPath, targetPath: VfsPath, folderName: string): Promise<VfsPath>;
  /** Move items into an existing folder. */
  moveInto(sources: readonly VfsPath[], folder: VfsPath): Promise<void>;
  /** Rename in place. §7.1 has no rename verb, so it is a move within the same parent. */
  rename(path: VfsPath, name: string): Promise<VfsPath>;
  /** A new empty folder, from the context menu. */
  newFolder(name: string): Promise<VfsPath>;
  /** Send an icon to the Trash (§7.8) — the same delete every other surface performs. */
  remove(path: VfsPath): Promise<void>;
  /**
   * Write the default desktop to disk, if it has not been written already (D-284).
   *
   * Called by every mutation below rather than at boot, so it is a user's first act of organising
   * that creates the files. Exposed for tests; nothing else needs to call it.
   */
  materialize(): Promise<void>;
  /**
   * Give an arranged desktop an icon for any app it has never been offered (D-285).
   *
   * Keyed on *offered*, not on *present*: an app whose icon the user deleted has been offered, so
   * it stays deleted. Without that distinction "keep the list in sync" and "a removed icon stays
   * removed" are the same operation with opposite answers, and sync would silently undo removal.
   *
   * A desktop that is still virtual needs nothing — it shows whatever exists, always.
   */
  syncApps(): Promise<void>;
  /**
   * Move `source` so it sits immediately before `before`, or last when `before` is `null` (D-287).
   *
   * The arrangement is saved per directory, so it survives a reload and rides along with sync.
   */
  reorder(source: VfsPath, before: VfsPath | null): Promise<void>;
  /**
   * Put the desktop back to the default arrangement (D-286).
   *
   * Everything on it goes to the Trash — §7.8, so it is recoverable — and the user is forgotten,
   * which makes the desktop virtual again and puts it back in step with the app list.
   */
  reset(): Promise<void>;
}

export interface DesktopDeps {
  vfs: VfsTree;
  bus: Bus;
  /** Which app ids exist right now — a shortcut to anything else is `unresolved`. */
  knownApp: (appId: string) => boolean;
  /** An app's display name, for the filename a new shortcut gets. */
  appName: (appId: string) => string;
  /**
   * Every app the user can currently see — the desktop a profile gets before it has one of its
   * own (D-284). §24.11 has already filtered this, so it never offers a gated game to a visitor.
   *
   * `category` is what sorts the games into their own group (D-285); the Arcade titles declare
   * `'Arcade'` and nothing else does.
   */
  apps: () => ReadonlyArray<{ id: string; name: string; category?: string }>;
  /** §7.8 — deleting goes to the Trash, exactly as it does in Explorer. */
  trash: (path: VfsPath) => Promise<unknown>;
  /** §14's store, holding which users' desktops have been written to disk (D-284). */
  materialized: {
    get(): readonly string[];
    set(users: readonly string[]): void;
  };
  /** What the games' group is called. From `copy.ts`, so the shell has one name for it. */
  arcadeFolderName: string;
  /** §14's store: per user, the app ids this desktop has already been offered (D-285). */
  offered: {
    get(): Readonly<Record<string, readonly string[]>>;
    set(next: Record<string, readonly string[]>): void;
  };
  /**
   * Flush §14 to storage, awaited (D-292).
   *
   * `settings.set` is debounced by 250 ms, which is right for a slider and wrong for a marker that
   * decides what the desktop *is*. Reset writes the marker and the view then reloads the page: the
   * two raced, the write lost, and the desktop came back arranged-and-empty instead of default.
   */
  persist: () => Promise<void>;
}

/** §24 — the category the Arcade titles declare, and the one that gets a group of its own. */
const ARCADE_CATEGORY = 'Arcade';

/**
 * Where a folder's hand-arranged order lives (D-287).
 *
 * A dotfile *inside the folder it orders*, so it is hidden from the grid for the same reason
 * every dotfile is (§11.1), it moves and syncs with the folder rather than being a second index
 * that can disagree with it, and a folder that has never been arranged simply has no file and
 * falls back to the default sort. Names rather than paths: renaming the folder must not lose it.
 */
const ORDER_FILE = '.order.json';

/** Folders first, then by name — the rule Explorer sorts by, so the two agree on sight (§11.1). */
function order(a: DesktopItem, b: DesktopItem): number {
  if (a.isDir !== b.isDir) return a.isDir ? -1 : 1;
  return a.label.localeCompare(b.label, undefined, { numeric: true });
}

export function createDesktopService(deps: DesktopDeps): DesktopService {
  const root = (): VfsPath => joinVfs(userRoot(deps.vfs.currentUser()), USER_FOLDERS[0]);

  /**
   * Turn a directory entry into an icon. A `.hlnk` is read to find its app; every failure along
   * that path — unreadable bytes, damaged JSON, an id nothing answers to — degrades to something
   * that still draws, because the alternative is a desktop that throws.
   */
  const decorate = async (entry: FileEntry): Promise<DesktopItem> => {
    if (entry.isDir) {
      return { path: entry.path, label: entry.name, kind: 'folder', isDir: true, mtime: entry.mtime };
    }
    const plain: DesktopItem = {
      path: entry.path,
      label: entry.name,
      kind: 'file',
      isDir: false,
      mtime: entry.mtime,
    };
    if (!isShortcutName(entry.name)) return plain;
    const link = await deps.vfs
      .readText(entry.path)
      .then(parseShortcut)
      .catch(() => null);
    // A `.hlnk` we cannot read is a file. It keeps its real name, extension and all, so what is
    // wrong stays visible rather than being disguised as a working icon.
    if (!link) return plain;
    return {
      path: entry.path,
      label: shortcutLabel(entry.name),
      kind: 'app',
      appId: link.app,
      isDir: false,
      mtime: entry.mtime,
      ...(deps.knownApp(link.app) ? {} : { unresolved: true }),
    };
  };

  const namesIn = async (dir: VfsPath): Promise<string[]> =>
    (await deps.vfs.list(dir).catch(() => [] as FileEntry[])).map((entry) => entry.name);

  /** The saved arrangement for `dir`, or an empty list when it has never been arranged. */
  const readOrder = async (dir: VfsPath): Promise<string[]> => {
    try {
      const parsed = JSON.parse(await deps.vfs.readText(joinVfs(dir, ORDER_FILE))) as {
        v?: number;
        order?: unknown;
      };
      if (parsed?.v !== 1 || !Array.isArray(parsed.order)) return [];
      return parsed.order.filter((name): name is string => typeof name === 'string');
    } catch {
      // Missing or damaged: the default sort, never an error. An arrangement is a convenience,
      // and losing it must not be able to stop the desktop drawing.
      return [];
    }
  };

  const writeOrder = async (dir: VfsPath, names: readonly string[]): Promise<void> => {
    await deps.vfs
      .write(joinVfs(dir, ORDER_FILE), JSON.stringify({ v: 1, order: names }, null, 2))
      .catch(() => undefined);
  };

  /**
   * Apply a saved arrangement.
   *
   * Anything the arrangement does not mention — a file that arrived since, an app that synced in —
   * sorts after what it does, by the default rule. That way a new icon appears in a predictable
   * place instead of at an arbitrary point in someone's layout.
   */
  const applyOrder = (items: DesktopItem[], saved: readonly string[]): DesktopItem[] => {
    if (saved.length === 0) return [...items].sort(order);
    const rank = new Map(saved.map((name, index) => [name, index]));
    return [...items].sort((a, b) => {
      const ai = rank.get(basename(a.path)) ?? Number.POSITIVE_INFINITY;
      const bi = rank.get(basename(b.path)) ?? Number.POSITIVE_INFINITY;
      return ai === bi ? order(a, b) : ai - bi;
    });
  };

  /** Has this user's desktop been written to disk yet (D-284)? */
  const isMaterialized = (): boolean => deps.materialized.get().includes(deps.vfs.currentUser());

  /**
   * The desktop a profile has before it has one of its own: every visible app, as icons that do
   * not exist on disk (D-284).
   *
   * Their paths are the paths the files *would* have, so an item's identity does not change when
   * the folder is written — the React key stays put and nothing re-mounts.
   */
  /** Shortcut icons for `apps`, inside `dir`. Names are made unique against each other. */
  const iconsFor = (
    apps: ReadonlyArray<{ id: string; name: string }>,
    dir: VfsPath,
  ): DesktopItem[] => {
    const taken = new Set<string>();
    return apps
      .map((app) => {
        const name = uniqueName(taken, shortcutFileName(app.name));
        taken.add(name);
        return {
          path: joinVfs(dir, name),
          label: shortcutLabel(name),
          kind: 'app' as const,
          appId: app.id,
          isDir: false,
          mtime: 0,
        };
      })
      .sort(order);
  };

  const isGame = (app: { category?: string }): boolean => app.category === ARCADE_CATEGORY;

  /** The games, as they would sit inside the Arcade folder (D-285). */
  const arcadeIcons = (): DesktopItem[] =>
    iconsFor(deps.apps().filter(isGame), joinVfs(root(), deps.arcadeFolderName));

  const defaultIcons = (): DesktopItem[] => {
    const dir = root();
    const apps = deps.apps();
    const games = apps.filter(isGame);
    const rest = apps.filter((app) => !isGame(app));
    const items = iconsFor(rest, dir);
    // The games go in a group of their own rather than thirteen deep down the alphabet, which is
    // what pushed the system apps off the old capped desktop in the first place (D-285).
    if (games.length > 0) {
      items.push({
        path: joinVfs(dir, deps.arcadeFolderName),
        label: deps.arcadeFolderName,
        kind: 'folder',
        isDir: true,
        mtime: 0,
      });
    }
    return items.sort(order);
  };

  /**
   * Every app that has a shortcut anywhere under the desktop, read straight off the disk.
   *
   * Deliberately **not** via `list()`, which would recurse back into the merge below. This is the
   * raw question — what is really there — and the merge needs it answered without its own help.
   */
  const presentOnDisk = async (): Promise<Set<string>> => {
    const found = new Set<string>();
    const walk = async (dir: VfsPath): Promise<void> => {
      for (const entry of await deps.vfs.list(dir).catch(() => [] as FileEntry[])) {
        if (entry.isDir) {
          await walk(entry.path);
          continue;
        }
        if (!isShortcutName(entry.name)) continue;
        const link = await deps.vfs
          .readText(entry.path)
          .then(parseShortcut)
          .catch(() => null);
        if (link) found.add(link.app);
      }
    };
    await walk(root());
    return found;
  };

  /**
   * The part of the default set that is genuinely missing, judged by **app identity** (D-289).
   *
   * Matching on filename was a bug with teeth: rename the games' group from "Arcade" to "Games"
   * and the default "Arcade" folder no longer looked taken, so the next listing helpfully added it
   * back and the desktop had both — one real and full, one virtual and empty.
   */
  const missingDefaults = async (): Promise<DesktopItem[]> => {
    const present = await presentOnDisk();
    const games = deps.apps().filter(isGame);
    const someGameMissing = games.some((game) => !present.has(game.id));
    return defaultIcons().filter((icon) =>
      icon.kind === 'folder' ? someGameMissing : !present.has(icon.appId as string),
    );
  };

  /**
   * The in-flight `materialize`, if there is one (D-301).
   *
   * Without this, a second caller sees the marker — which is set *before* the writes, deliberately
   * — and returns while the files are still being written. "Materialized" then means "someone has
   * started", not "the files exist", and the next mutation computes a name against a half-written
   * directory. That is how `remove` then `add` produced **Calculator and Calculator (2)**.
   */
  let materializing: Promise<void> | null = null;

  /**
   * Mutations run one at a time.
   *
   * Every one of them is read-then-write — list the directory, pick a free name, write it — and
   * two interleaved copies of that pattern pick the same name or skip one. The view fires these
   * from click handlers that do not await each other, so the ordering cannot be left to the caller.
   */
  let tail: Promise<unknown> = Promise.resolve();
  /**
   * Re-entrant: `group` calls `moveInto`, and a queue that made the inner call wait for the outer
   * one would deadlock. Only one section runs at a time, so the flag is never read across an
   * interleaving of two different sections.
   */
  let inside = false;
  const serial = <T,>(work: () => Promise<T>): Promise<T> => {
    if (inside) return work();
    const run = async (): Promise<T> => {
      inside = true;
      try {
        return await work();
      } finally {
        inside = false;
      }
    };
    const next = tail.then(run, run);
    // A failed mutation must not poison the queue for the next one.
    tail = next.catch(() => undefined);
    return next;
  };


  /** The actual write of the default set. Only ever called through `materialize`. */
  async function write(): Promise<void> {
    const user = deps.vfs.currentUser();
    const done = deps.materialized.get();
    // Marked *before* the writes: a materialize that half-failed must not run again on the next
    // mutation and lay a second copy of every icon it did manage.
    deps.materialized.set([...done, user]);
    const dir = root();
    await deps.vfs.mkdir(dir).catch(() => undefined);

    // Every name is decided **first**, from one listing, and only then is anything written. That
    // is what makes the writes safe to run together: `uniqueName` never races itself, because it
    // has finished before the first byte goes out.
    const taken = new Set(await namesIn(dir));
    const files: Array<{ path: VfsPath; app: string }> = [];
    let arcade: VfsPath | null = null;

    for (const item of defaultIcons()) {
      const name = uniqueName(taken, basename(item.path));
      taken.add(name);
      if (item.kind === 'folder') {
        // The games' group, as a real directory with the games really inside it (D-285).
        arcade = joinVfs(dir, name);
        continue;
      }
      files.push({ path: joinVfs(dir, name), app: item.appId as string });
    }

    if (arcade !== null) {
      // The directory has to exist before anything is written into it; its contents do not have to
      // wait on each other.
      await deps.vfs.mkdir(arcade).catch(() => undefined);
      const inside = new Set<string>();
      for (const game of arcadeIcons()) {
        const gameName = uniqueName(inside, basename(game.path));
        inside.add(gameName);
        files.push({ path: joinVfs(arcade, gameName), app: game.appId as string });
      }
    }

    // Concurrently, not one at a time. Twenty-odd sequential round trips through the OPFS worker
    // is a visible pause on the user's first act of arranging the desktop, and under load it was
    // slow enough to time out a 90-second e2e. One failed write must not abandon the rest.
    await Promise.all(
      files.map((file) =>
        deps.vfs.write(file.path, serializeShortcut(file.app)).catch(() => undefined),
      ),
    );

    // Everything on the desktop now has been offered, so none of it counts as new (D-285).
    deps.offered.set({ ...deps.offered.get(), [user]: deps.apps().map((app) => app.id) });
    // Both markers reach storage before this resolves (D-292) — a reload a moment later must not
    // find a desktop whose files exist and whose flag says they do not.
    await deps.persist();
  }

  const service: DesktopService = {
    root,

    async list(dir) {
      const target = dir ?? root();
      // The folder is created by `bootstrap` (§7.1), but a listing that races it — or a user who
      // deleted it — must produce an empty desktop, not an error.
      const entries = await deps.vfs.list(target).catch(() => [] as FileEntry[]);
      const items = await Promise.all(
        // Dotfiles are hidden here for the reason Explorer hides them (§11.1) — `.Trash` is one.
        entries.filter((entry) => !entry.name.startsWith('.')).map(decorate),
      );
      // A profile that has never organised its desktop is shown the default one, *merged* with
      // whatever is really there — because §7.7's host-OS drop can put a file on a desktop nobody
      // has arranged yet, and showing the file alone would make every app icon vanish. Real
      // entries win a name collision; they exist and the defaults do not.
      //
      // Only at the top level, and only until something is written: an emptied desktop stays
      // empty, because by then the user has said what they want it to hold.
      const saved = await readOrder(target);
      if (dir === undefined && !isMaterialized()) {
        return applyOrder([...items, ...(await missingDefaults())], saved);
      }
      // Opening the games' group before anything has been written to disk. The folder is one of
      // the default icons, so it has to be openable like any other — otherwise the games are
      // visible on a fresh desktop and unreachable, which is worse than not grouping them.
      if (dir !== undefined && !isMaterialized() && dir === joinVfs(root(), deps.arcadeFolderName)) {
        const present = await presentOnDisk();
        return applyOrder(
          [...items, ...arcadeIcons().filter((icon) => !present.has(icon.appId as string))],
          saved,
        );
      }
      return applyOrder(items, saved);
    },

    async materialize() {
      // A concurrent caller waits for the writes rather than racing them (D-301).
      if (materializing !== null) return materializing;
      const user = deps.vfs.currentUser();
      const done = deps.materialized.get();
      if (done.includes(user)) return;
      materializing = write();
      try {
        await materializing;
      } finally {
        materializing = null;
      }
    },


    subscribe(handler) {
      // Two sources, because they answer different questions. `watch` covers this tab's writes and
      // the cross-tab replay of §18 F16. `app:registryChanged` covers a **sign-in**, which changes
      // which folder the desktop even *is* — so the watch is torn down and re-established against
      // the new root rather than left pointing at the guest's.
      let offWatch = deps.vfs.watch(root(), handler);
      let watching = root();
      const offRegistry = deps.bus.on('app:registryChanged', () => {
        if (watching !== root()) {
          offWatch();
          watching = root();
          offWatch = deps.vfs.watch(watching, handler);
        }
        handler();
      });
      return () => {
        offWatch();
        offRegistry();
      };
    },

    async appsPresent() {
      // **Recursively**, because an app dragged into a group is still on the desktop. Counting
      // only the top level would make the Add menu offer every game the moment they were grouped,
      // and would make D-285's sync re-add them.
      const present = new Set<string>();
      // A listing that fails contributes nothing rather than abandoning the walk (D-394). The
      // caller is the Add-app menu, whose whole job is to answer "what is *not* here" — and an
      // answer of "everything is here" derived from a thrown read is the one wrong answer that
      // looks exactly like a correct one.
      const walk = async (dir: VfsPath): Promise<void> => {
        const items = await service
          .list(dir === root() ? undefined : dir)
          .catch(() => [] as DesktopItem[]);
        for (const item of items) {
          if (item.kind === 'app' && item.appId !== undefined) present.add(item.appId);
          if (item.isDir) await walk(item.path);
        }
      };
      await walk(root());
      return present;
    },

    async isFolder(path) {
      if ((await deps.vfs.stat(path).catch(() => null))?.isDir === true) return true;
      // The games' group before anything has been written: a default icon with no directory behind
      // it yet, and it still has to open (D-289).
      return !isMaterialized() && path === joinVfs(root(), deps.arcadeFolderName);
    },

    async syncApps() {
      const user = deps.vfs.currentUser();
      // A virtual desktop already shows whatever exists; there is nothing to write and nothing
      // that could drift.
      if (!isMaterialized()) return;

      const offered = deps.offered.get();
      const seen = offered[user];
      const all = deps.apps();
      if (seen === undefined) {
        // Arranged before this desktop tracked what it had offered. Adopt the current list and
        // write nothing: everything here predates the feature, so none of it is *new*.
        deps.offered.set({ ...offered, [user]: all.map((app) => app.id) });
        await deps.persist();
        return;
      }

      const fresh = all.filter((app) => !seen.includes(app.id));
      if (fresh.length === 0) return;
      deps.offered.set({ ...offered, [user]: [...seen, ...fresh.map((app) => app.id)] });
      await deps.persist();

      // A game joins the group if the group is still there; if the user has renamed or deleted it,
      // the game lands on the desktop rather than resurrecting a folder they got rid of.
      const dir = root();
      const arcade = joinVfs(dir, deps.arcadeFolderName);
      const hasArcade = (await deps.vfs.stat(arcade).catch(() => null))?.isDir === true;
      for (const app of fresh) {
        const into = isGame(app) && hasArcade ? arcade : dir;
        const name = uniqueName(await namesIn(into), shortcutFileName(app.name));
        await deps.vfs.write(joinVfs(into, name), serializeShortcut(app.id)).catch(() => undefined);
      }
    },

    async reorder(source, before) {
      // A rearrangement is an arrangement: the files have to exist before their order can mean
      // anything, so this materializes exactly as every other mutation does.
      await service.materialize();
      const dir = parentOf(source);
      if (before !== null && parentOf(before) !== dir) return;

      // Start from what is on screen now, so an unsaved default arrangement is preserved rather
      // than reshuffled the first time one icon is moved.
      const shown = (await service.list(dir === root() ? undefined : dir)).map((item) =>
        basename(item.path),
      );
      const name = basename(source);
      const without = shown.filter((entry) => entry !== name);
      const at = before === null ? without.length : without.indexOf(basename(before));
      if (at < 0) return;
      await writeOrder(dir, [...without.slice(0, at), name, ...without.slice(at)]);
    },

    async reset() {
      const user = deps.vfs.currentUser();
      // To the Trash, never deleted (§7.8) — a reset that ate a document the user had left on the
      // desktop would be the one destructive thing in the shell that does not ask twice.
      for (const entry of await deps.vfs.list(root()).catch(() => [] as FileEntry[])) {
        await deps.trash(entry.path).catch(() => undefined);
      }
      deps.materialized.set(deps.materialized.get().filter((id) => id !== user));
      const offered = { ...deps.offered.get() };
      delete offered[user];
      deps.offered.set(offered);
      // Awaited, because the view reloads the page the instant this resolves (D-292). Without it
      // the debounced write loses the race and the desktop comes back arranged — and empty.
      await deps.persist();
    },

    addApp: (appId) =>
      serial(async () => {
        await service.materialize();
        const dir = root();
        const name = uniqueName(await namesIn(dir), shortcutFileName(deps.appName(appId)));
        const path = joinVfs(dir, name);
        await deps.vfs.write(path, serializeShortcut(appId));
        return path;
      }),

    async moveInto(sources, folder) {
      await service.materialize();
      const taken = new Set(await namesIn(folder));
      for (const source of sources) {
        // Moving a folder into itself, or onto its own parent, is not a move.
        if (source === folder || parentOf(folder) === source) continue;
        const name = uniqueName(taken, basename(source));
        taken.add(name);
        // One item that will not move must not abandon the rest of the drop.
        await deps.vfs.move(source, joinVfs(folder, name)).catch(() => undefined);
      }
    },

    async group(sourcePath, targetPath, folderName) {
      if (sourcePath === targetPath) return targetPath;
      await service.materialize();
      const target = await deps.vfs.stat(targetPath).catch(() => null);
      // Onto a folder: an ordinary move in, and the folder keeps the name it already has.
      if (target?.isDir) {
        await service.moveInto([sourcePath], targetPath);
        return targetPath;
      }
      const dir = parentOf(targetPath);
      const folder = joinVfs(dir, uniqueName(await namesIn(dir), folderName));
      await deps.vfs.mkdir(folder);
      // Target first, so the folder reads left-to-right as "what was here, then what you dropped".
      await service.moveInto([targetPath, sourcePath], folder);
      return folder;
    },

    async rename(path, name) {
      await service.materialize();
      const dir = parentOf(path);
      const current = basename(path);
      const trimmed = name.trim();
      if (trimmed.length === 0) return path;
      // A shortcut's extension is an implementation detail the label never showed, so renaming
      // "Calculator" to "Sums" must not turn the icon into a plain file called `Sums`.
      const desired = isShortcutName(current) ? shortcutFileName(trimmed) : trimmed;
      if (desired === current) return path;
      const taken = (await namesIn(dir)).filter((existing) => existing !== current);
      const moved = await deps.vfs.move(path, joinVfs(dir, uniqueName(taken, desired)));
      // The arrangement is by name, so a rename that did not update it would send the icon to the
      // end of the desktop — which looks like the rename moved it.
      const saved = await readOrder(dir);
      if (saved.includes(current)) {
        await writeOrder(
          dir,
          saved.map((entry) => (entry === current ? basename(moved.path) : entry)),
        );
      }
      return moved.path;
    },

    newFolder: (name) =>
      serial(async () => {
        await service.materialize();
        const dir = root();
        await deps.vfs.mkdir(dir).catch(() => undefined);
        const folder = joinVfs(dir, uniqueName(await namesIn(dir), name));
        await deps.vfs.mkdir(folder);
        return folder;
      }),

    remove: (path) =>
      serial(async () => {
        await service.materialize();
        await deps.trash(path);
      }),

  };

  return service;
}
